/**
 * Grouping helper for the Inbox "group by customer" view. Pure presentation-side
 * grouping — conversations stay separate threads; this only organizes the list.
 */

export interface GroupableConversation {
  id: string
  customer_id: string | null
  unread_count: number
  last_message_at: string | null
}

export interface CustomerConversationGroup<T extends GroupableConversation> {
  customerId: string
  customerName: string
  conversations: T[]
  unreadCount: number
  latestActivity: string | null
}

export interface GroupedConversations<T extends GroupableConversation> {
  /** Conversations with no customer — shown flat, above the customer groups. */
  unassigned: T[]
  /** One group per customer, sorted by most recent activity. */
  groups: Array<CustomerConversationGroup<T>>
}

export function groupConversationsByCustomer<T extends GroupableConversation>(
  conversations: T[],
  customerNameById: Map<string, string>
): GroupedConversations<T> {
  const unassigned: T[] = []
  const byCustomer = new Map<string, T[]>()

  for (const convo of conversations) {
    if (!convo.customer_id) {
      unassigned.push(convo)
      continue
    }
    const list = byCustomer.get(convo.customer_id)
    if (list) list.push(convo)
    else byCustomer.set(convo.customer_id, [convo])
  }

  const groups: Array<CustomerConversationGroup<T>> = []
  byCustomer.forEach((convos, customerId) => {
    let unreadCount = 0
    let latestActivity: string | null = null
    for (const c of convos) {
      unreadCount += c.unread_count || 0
      if (c.last_message_at && (!latestActivity || c.last_message_at > latestActivity)) {
        latestActivity = c.last_message_at
      }
    }
    groups.push({
      customerId,
      customerName: customerNameById.get(customerId) || 'Unknown customer',
      conversations: convos,
      unreadCount,
      latestActivity,
    })
  })

  groups.sort((a, b) => (b.latestActivity || '').localeCompare(a.latestActivity || ''))
  return { unassigned, groups }
}
